import { isEmailValid } from '@juvare/common-form-ui/WidgetFormEmail';
import { useAlertUiTranslation } from '../../../../../hooks';
import { useFormRow } from '../Components/useFormRow';
export function useAddRecipientsStep(mode, config, followUp) {
    const { t } = useAlertUiTranslation();
    const formConfig = [
        {
            type: 'form.panel',
            key: '!recipientsPanel',
            style: {
                layout: 'column',
                paddingTop: config === null || config === void 0 ? void 0 : config.paddingTop,
                surface: 'level1',
            },
            children: [
                useFormRow([
                    {
                        type: 'form.content',
                        key: 'content',
                        template: {
                            data: '',
                            content: `<div style="display: flex; align-items: center"> <span class="icon-person-add-outlined grey-700"></span> <p style="font-size: 20px; padding-left: 16px" class="subtitle1"> ${t('wizard.addRecipients.internalRecipients')}</p></div>`,
                        },
                    },
                    {
                        type: 'alert.recipients',
                        key: 'recipients.internal',
                        style: {
                            paddingLeft: config.mode === 'edit' ? 56 : 49,
                        },
                        label: t('wizard.addRecipients.recipients'),
                        followUp: followUp,
                        validate: {
                            custom: (value, handler) => {
                                var _a;
                                if (value === undefined && ((_a = handler.formData.recipients) === null || _a === void 0 ? void 0 : _a.external) === undefined) {
                                    return t('wizard.addRecipients.required');
                                }
                                return;
                            },
                        },
                    },
                ], config.mode),
            ],
        },
        {
            type: 'form.panel',
            style: {
                layout: 'column',
                surface: 'level1',
            },
            hidden: {
                custom: (handler) => {
                    return !handler.formParams.features.enableExternalRecipients;
                },
            },
            children: [
                useFormRow([
                    {
                        type: 'form.content',
                        key: 'content',
                        template: {
                            data: '',
                            content: `<div style="display: flex; align-items: center"> <span class="icon-group-add-outlined grey-700"></span> <p style="font-size: 20px; padding-left: 16px" class="subtitle1"> ${t('wizard.addRecipients.externalRecipients')}</p></div>`,
                        },
                    },
                    {
                        type: 'alert.externalRecipients',
                        key: 'recipients.external',
                        style: {
                            paddingLeft: config.mode === 'edit' ? 56 : 49,
                        },
                        validate: {
                            custom: (value, handler) => {
                                var _a;
                                if (value === undefined && ((_a = handler.formData.recipients) === null || _a === void 0 ? void 0 : _a.internal) === undefined) {
                                    return t('wizard.addRecipients.required');
                                }
                                return;
                            },
                        },
                    },
                ], config.mode),
            ],
        },
        {
            type: 'form.panel',
            style: {
                layout: 'column',
                surface: 'level1',
            },
            hidden: {
                custom: (handler) => {
                    var _a;
                    if (handler.formParams.features.emailCc === false) {
                        delete handler.formData.emailCc;
                        return true;
                    }
                    else if ((_a = handler.formData) === null || _a === void 0 ? void 0 : _a.channels) {
                        const hideCcEmail = Array.isArray(handler.formData.channels) &&
                            !handler.formData.channels.some((channel) => channel === 'email');
                        if (hideCcEmail) {
                            delete handler.formData.emailCc;
                        }
                        return hideCcEmail;
                    }
                    return !handler.formParams.features.emailCc;
                },
            },
            children: [
                useFormRow([
                    {
                        type: 'form.content',
                        key: 'content',
                        template: {
                            data: '',
                            content: `<div style="display: flex; align-items: center"> <span class="icon-forward-to-inbox-outlined grey-700"></span> <p style="font-size: 20px; padding-left: 16px" class="subtitle1"> ${t('wizard.addRecipients.emailCc')}</p></div>`,
                        },
                    },
                    {
                        type: 'form.panel',
                        style: {
                            layout: 'row',
                            horizontalAlign: 'center',
                            verticalAlign: 'center',
                            paddingLeft: config.mode === 'edit' ? 56 : 49,
                        },
                        children: [
                            {
                                type: 'form.text',
                                key: 'emailCc',
                                label: t('wizard.addRecipients.addEmailAddressesForCC'),
                                validate: {
                                    custom: (value) => {
                                        const errorMessages = [];
                                        if (value !== undefined && value !== null && value !== '') {
                                            if (value.endsWith(',')) {
                                                value = value.slice(0, -1);
                                            }
                                            const emails = value.split(',').map((email) => email.trim());
                                            for (const email of emails) {
                                                if (!isEmailValid(email)) {
                                                    errorMessages.push(t('wizard.addRecipients.invalidEmailAddressError'));
                                                    break;
                                                }
                                            }
                                            const uniqueEmails = new Set(emails);
                                            if (uniqueEmails.size !== emails.length) {
                                                errorMessages.push(t('wizard.addRecipients.duplicateEmailAddressError'));
                                            }
                                            if (emails.length > 30) {
                                                errorMessages.push(t('wizard.addRecipients.emailCountError'));
                                            }
                                        }
                                        return errorMessages.length > 0 ? errorMessages.join(' ') : undefined;
                                    },
                                },
                                event: {
                                    onChange: (value, handler) => {
                                        const clearEmailCCButton = handler.up().down('clearEmailCc');
                                        if (clearEmailCCButton) {
                                            if (value !== undefined && value !== '') {
                                                clearEmailCCButton.config.disabled = false;
                                            }
                                            else {
                                                clearEmailCCButton.config.disabled = true;
                                            }
                                            clearEmailCCButton.update();
                                        }
                                    },
                                },
                            },
                            {
                                type: 'form.button',
                                key: 'clearEmailCc',
                                label: t('wizard.addRecipients.removeEmailAddressesForCCButton'),
                                disabled: true,
                                icon: 'icon-remove',
                                style: {
                                    type: 'iconButton',
                                    width: 40,
                                },
                                event: {
                                    onClick: (handler) => {
                                        var _a;
                                        (_a = handler.up().down('emailCc')) === null || _a === void 0 ? void 0 : _a.setData(undefined);
                                        handler.config.disabled = true;
                                    },
                                },
                            },
                        ],
                    },
                ], config.mode),
            ],
        },
        {
            type: 'form.panel',
            style: {
                layout: 'row',
                horizontalAlign: 'left',
                surface: 'level1',
            },
            children: [
                useFormRow([
                    {
                        type: 'form.content',
                        key: 'content',
                        style: {
                            paddingTop: 16,
                        },
                        template: {
                            data: '',
                            content: `<p style="display: flex; align-items: center; font-size: 20px" class="subtitle1"> ${t('wizard.addRecipients.totalRecipients')}</p>`,
                        },
                    },
                    {
                        type: 'form.panel',
                        style: {
                            paddingLeft: config.mode === 'edit' ? 56 : 49,
                        },
                        children: [
                            {
                                type: 'alert.totalRecipients',
                                key: 'totalRecipients',
                                validate: {
                                    requireRecipients: mode !== 'template',
                                    requireRecipientsMessage: t('pages.edit.recipients.dialog.noneOfTheRecipientsHaveContactDetails'),
                                },
                            },
                            {
                                type: 'form.content',
                                text: t('wizard.addRecipients.totalRecipientsExplanationText'),
                                style: {
                                    alert: 'info',
                                },
                            },
                        ],
                    },
                ], config.mode),
            ],
        },
    ];
    if (!(config === null || config === void 0 ? void 0 : config.hideHeader)) {
        formConfig.unshift({
            type: 'form.content',
            key: 'content1',
            style: {
                paddingTop: 8,
            },
            html: `<h3>${t('wizard.addRecipients.addRecipients')}</h3>`,
        });
    }
    return {
        label: t('wizard.addRecipients.addRecipients'),
        form: {
            type: 'form',
            children: formConfig,
        },
    };
}
